import React from 'react';
import { Button } from '@/components/ui/button';
import { MessageSquare } from 'lucide-react';

interface NotesIndicatorProps {
  hasAdvisorNotes: boolean;
  hasTechnicianNotes: boolean;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}

export const NotesIndicator: React.FC<NotesIndicatorProps> = ({
  hasAdvisorNotes,
  hasTechnicianNotes,
  onClick,
  disabled = false,
  className = ''
}) => {
  const hasAnyNotes = hasAdvisorNotes || hasTechnicianNotes;

  // Determine icon color and style based on notes presence
  const getIconStyle = () => {
    if (hasAdvisorNotes && hasTechnicianNotes) {
      return 'text-purple-600 dark:text-purple-400'; // Both types
    } else if (hasAdvisorNotes) {
      return 'text-blue-600 dark:text-blue-400'; // Advisor only
    } else if (hasTechnicianNotes) {
      return 'text-orange-600 dark:text-orange-400'; // Technician only
    } else {
      return 'text-muted-foreground'; // No notes
    }
  };

  const getTooltipText = () => {
    if (hasAdvisorNotes && hasTechnicianNotes) {
      return 'Has both advisor and technician notes - click to view/edit';
    } else if (hasAdvisorNotes) {
      return 'Has advisor notes - click to view/edit';
    } else if (hasTechnicianNotes) {
      return 'Has technician notes - click to view/edit';
    } else {
      return 'Add notes for this recall';
    }
  };

  return (
    <Button
      variant='ghost'
      size='sm'
      onClick={onClick}
      disabled={disabled}
      className={`h-8 w-8 p-0 relative ${className}`}
      title={getTooltipText()}
    >
      <MessageSquare className={`h-4 w-4 ${getIconStyle()}`} />

      {/* Indicator dots for different note types */}
      {hasAnyNotes && (
        <div className='absolute -top-1 -right-1 flex gap-0.5'>
          {hasAdvisorNotes && (
            <div className='w-2 h-2 rounded-full bg-blue-500 border border-white dark:border-gray-800' />
          )}
          {hasTechnicianNotes && (
            <div className='w-2 h-2 rounded-full bg-orange-500 border border-white dark:border-gray-800' />
          )}
        </div>
      )}
    </Button>
  );
};

export default NotesIndicator;
